/* Owns the panel session: the access token, the bootstrapped UserDto, the
   accessible server summaries and which server is active. A module-level store
   rather than React state so `lib/api.ts` and `lib/ws.ts` — neither of which may
   depend on React — can read and write it. */

import type { MeServerSummary, Role, UserDto } from '@sentinelac/shared'
import { useSyncExternalStore } from 'react'

/** `unknown` until the boot refresh settles, so guards never bounce a valid session to /login. */
export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous'

export interface AuthState {
  /**
   * SPEC §10.3: the access token lives in memory only — never localStorage,
   * never a JS-readable cookie, never a URL except the WebSocket handshake.
   * A reload therefore has no token and must re-obtain one from the `sac_rt`
   * HttpOnly refresh cookie.
   */
  accessToken: string | null
  user: UserDto | null
  servers: MeServerSummary[]
  activeServerId: string | null
  status: SessionStatus
}

const initialState: AuthState = {
  accessToken: null,
  user: null,
  servers: [],
  activeServerId: null,
  status: 'unknown',
}

let state: AuthState = initialState
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getAuthState(): AuthState {
  return state
}

function set(patch: Partial<AuthState>): void {
  const next = { ...state, ...patch }
  // Reference equality is the store's change signal; skip a no-op write so
  // useSyncExternalStore does not re-render every subscriber for nothing.
  let changed = false
  for (const key of Object.keys(patch) as Array<keyof AuthState>) {
    if (state[key] !== next[key]) changed = true
  }
  if (!changed) return
  state = next
  emit()
}

export function setAccessToken(token: string | null): void {
  set({ accessToken: token })
}

export function getAccessToken(): string | null {
  return state.accessToken
}

export function setSession(input: { accessToken: string; user: UserDto }): void {
  set({ accessToken: input.accessToken, user: input.user, status: 'authenticated' })
}

export function setUser(user: UserDto): void {
  set({ user, status: 'authenticated' })
}

export function setServers(servers: MeServerSummary[]): void {
  set({ servers })
}

export function setActiveServerId(serverId: string | null): void {
  set({ activeServerId: serverId })
}

export function markAnonymous(): void {
  set({ accessToken: null, user: null, servers: [], activeServerId: null, status: 'anonymous' })
}

/** Full reset, used by logout and by an unrecoverable refresh failure. */
export function clearAuth(): void {
  markAnonymous()
}

/** Test seam: puts the store back to its boot state between cases. */
export function resetAuthStoreForTests(): void {
  state = initialState
  emit()
}

export function useAuthState(): AuthState {
  return useSyncExternalStore(subscribe, getAuthState, getAuthState)
}

export function useCurrentUser(): UserDto | null {
  return useAuthState().user
}

/** The caller's role on one server, from the `GET /v1/auth/me` projection. */
export function roleForServer(serverId: string | null | undefined): Role | null {
  if (!serverId) return null
  return state.servers.find((s) => s.id === serverId)?.role ?? null
}

export function useRoleForServer(serverId: string | null | undefined): Role | null {
  const { servers } = useAuthState()
  if (!serverId) return null
  return servers.find((s) => s.id === serverId)?.role ?? null
}

export { subscribe as subscribeAuthStore }
